import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../../components/Admin/AdminActionButton';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import PaginationButtons from '../../../components/Common/Pagination';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import {
  ClassroomListItem,
  getAdminClassrooms,
} from './services/Classrooms.service';
import { AdminNavHandler } from '../adminNav';
import ClassroomDetailsView from './ClassroomDetailsView';
import ProjectDetailView from './ProjectDetailView';

type SortKey = 'name' | 'books' | 'created';
type SortDir = 'asc' | 'desc';

// Only Name and Created are sorted server-side (cheap, indexed). Books is a
// client-side reorder of the visible page only — surfacing the busiest
// classroom on the current page is the most common admin need.
const SERVER_SORT_KEYS: SortKey[] = ['name', 'created'];
const isServerSort = (k: SortKey) => SERVER_SORT_KEYS.includes(k);

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '-';
  const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

interface ClassroomsProps {
  onOptionClick: AdminNavHandler;
  initialTeacherEmail?: string;
  initialClassroomId?: string;
}

const Classrooms: React.FC<ClassroomsProps> = ({
  onOptionClick,
  initialTeacherEmail,
  initialClassroomId,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Drill-down state lives in the URL so reload + share work.
  const drillProjectId = searchParams.get('projectId');
  const drillClassroomId = searchParams.get('classroomId') || initialClassroomId || '';

  // Table state
  const [classrooms, setClassrooms] = useState<ClassroomListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Filters
  const [teacherFilter] = useState(initialTeacherEmail || '');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) navigate('/admin');
  }, [navigate]);

  const loadClassrooms = useCallback(
    async (
      page: number,
      teacher?: string,
      search?: string,
      sortByKey?: SortKey,
      sortDirVal?: SortDir,
    ) => {
      setLoading(true);
      const filters: {
        teacherEmail?: string;
        search?: string;
        sortBy?: 'name' | 'created';
        sortDir?: 'asc' | 'desc';
      } = {};
      if (teacher?.trim()) filters.teacherEmail = teacher.trim();
      if (search?.trim()) filters.search = search.trim();
      if (sortByKey && isServerSort(sortByKey)) {
        filters.sortBy = sortByKey as 'name' | 'created';
        filters.sortDir = sortDirVal;
      }

      const result = await getAdminClassrooms(page, filters);
      setClassrooms(result.classrooms);
      setTotalPages(result.totalPages);
      setTotalRecords(result.totalRecords);
      setPageSize(result.pageSize);
      setLoading(false);
    },
    [],
  );

  // Fetch on mount + page / server-sort change
  useEffect(() => {
    loadClassrooms(currentPage, teacherFilter, searchTerm || undefined, sortKey, sortDir);
  }, [currentPage, isServerSort(sortKey) ? sortKey : null, isServerSort(sortKey) ? sortDir : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number | string) => {
    const p = typeof page === 'string' ? parseInt(page, 10) : page;
    if (p > 0 && p <= totalPages) setCurrentPage(p);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    loadClassrooms(1, teacherFilter, term || undefined, sortKey, sortDir);
  };

  const handleSort = (key: string) => {
    const k = key as SortKey;
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  // Books is the only client-side sort (visible page only). Name/Created are
  // server-sorted so the order is global.
  const sortedClassrooms = useMemo(() => {
    if (isServerSort(sortKey)) return classrooms;
    const dirMul = sortDir === 'asc' ? 1 : -1;
    return [...classrooms].sort(
      (a, b) => ((a.bookCount || 0) - (b.bookCount || 0)) * dirMul,
    );
  }, [classrooms, sortKey, sortDir]);

  // ── URL drill-down helpers ─────────────────────────────────────────────────
  const updateParams = (mutator: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    setSearchParams(next, { replace: false });
  };

  const openClassroom = (id: string) =>
    updateParams((p) => {
      p.set('classroomId', id);
      p.delete('projectId');
    });
  const openProject = (id: string) => updateParams((p) => p.set('projectId', id));
  const closeProject = () => updateParams((p) => p.delete('projectId'));
  const closeClassroom = () =>
    updateParams((p) => {
      p.delete('classroomId');
      p.delete('projectId');
    });

  // ── Render ─────────────────────────────────────────────────────────────────
  if (drillProjectId) {
    return (
      <ProjectDetailView
        projectId={drillProjectId}
        onClose={closeProject}
        onOptionClick={onOptionClick}
        onOpenClassroom={openClassroom}
      />
    );
  }

  if (drillClassroomId) {
    return (
      <ClassroomDetailsView
        classroomId={drillClassroomId}
        onClose={closeClassroom}
        onOptionClick={onOptionClick}
        onOpenProject={openProject}
      />
    );
  }

  return (
    <div className="p-0 md:p-4">
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <AdminSearchInput
          placeholder="Search name, teacher, code, ID..."
          onSearch={handleSearch}
          className="w-64"
        />
      </div>

      {!isServerSort(sortKey) && (
        <div className="text-xs text-gray-500 mb-2 italic">
          Sorting by Books reorders the visible page only — use Name or Created for a global sort.
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="min-w-full border border-gray-300">
            <AdminTableHeader
              sortBy={sortKey}
              sortDirection={sortDir}
              onSort={handleSort}
              columns={[
                { key: 'sn', label: 'S#' },
                { key: 'name', label: 'Name', sortable: true },
                { key: 'teacher', label: 'Teacher' },
                { key: 'groupCode', label: 'Group Code' },
                { key: 'students', label: 'Students' },
                { key: 'projects', label: 'Projects' },
                { key: 'books', label: 'Books', sortable: true },
                { key: 'created', label: 'Created', sortable: true },
                { key: 'actions', label: 'Actions' },
              ]}
            />
            <tbody>
              {sortedClassrooms.length > 0 ? (
                sortedClassrooms.map((classroom, index) => (
                  <tr key={classroom.id} className={`text-sm ${index % 2 === 0 ? 'bg-gray-100' : ''}`}>
                    <td className="text-left py-2 px-4 border-b">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="text-left py-2 px-4 border-b font-medium">
                      <button
                        type="button"
                        className="text-storygreen underline hover:text-storyblue cursor-pointer"
                        title="View classroom details"
                        onClick={(e) => { e.stopPropagation(); openClassroom(classroom.id); }}
                      >
                        {classroom.name}
                      </button>
                    </td>
                    <td className="text-left py-2 px-4 border-b">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline text-sm"
                        onClick={(e) => { e.stopPropagation(); onOptionClick('Customers', { teacherEmail: classroom.teacherEmail }); }}
                        title="View teacher"
                      >
                        {classroom.teacherEmail}
                      </button>
                    </td>
                    <td className="text-left py-2 px-4 border-b font-mono text-xs">
                      {classroom.groupCode || '-'}
                    </td>
                    <td className="text-left py-2 px-4 border-b">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={(e) => { e.stopPropagation(); onOptionClick('Students', { classroomId: classroom.id }); }}
                        title="View students"
                      >
                        {classroom.studentCount}
                      </button>
                    </td>
                    <td className="text-left py-2 px-4 border-b">{classroom.projectCount}</td>
                    <td className="text-left py-2 px-4 border-b font-medium">
                      {classroom.bookCount ?? 0}
                    </td>
                    <td className="text-left py-2 px-4 border-b text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(classroom.createdAt)}
                    </td>
                    <td className="text-left py-2 px-4 border-b">
                      <AdminActionButton
                        icon={faEye}
                        title="View details"
                        hoverColor="hover:text-indigo-600"
                        onClick={() => openClassroom(classroom.id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecordFound colSpan={9} />
              )}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4">
            <TableInfoBar
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              totalRecords={totalRecords}
              recordsOnPage={classrooms.length}
            />
            {totalPages > 1 && (
              <PaginationButtons
                totalPages={totalPages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Classrooms;
